// src/components/admin/Decisioned.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  Download,
  Calendar,
  Mail,
  User,
  Tag,
  Paperclip,
  Check,
  Filter,
  Grid,
  List,
  RefreshCw,
  Users,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Inbox,
  X,
  Award,
  Archive,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import RichTextViewer from '../common/RichTextViewer';
import { showToast } from '../../utils/toast';
import { handleDownloadFile } from '../../utils/utils.global';

// Function to parse Quill Delta format to HTML (if needed)
const parseQuillContent = (content) => {
  if (!content) return '';

  try {
    // If it's already a string with HTML, return as is
    if (typeof content === 'string' && (content.includes('<p>') || content.includes('<div>'))) {
      return content;
    }

    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      let html = '';
      content.ops.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      return html;
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (parsed.ops) {
          return parseQuillContent(parsed);
        }
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

const Decisioned = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterDecision, setFilterDecision] = useState('all'); // 'all', 'accepted', 'rejected'
  const [filterType, setFilterType] = useState('all');
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('decisioned_view');
    return saved || 'table';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showActionsDropdown, setShowActionsDropdown] = useState(null);
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveSubmissionId, setArchiveSubmissionId] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [reviewsManuscript, setReviewsManuscript] = useState(null);
  const [reviewsData, setReviewsData] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedReviewIds, setSelectedReviewIds] = useState([]);
  const [exportingReviews, setExportingReviews] = useState(null);
  
  // Data states
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [decisionStats, setDecisionStats] = useState({
    accepted: 0,
    rejected: 0,
    returned_for_correction: 0,
    returned_for_revision: 0
  });
  const [articleTypes, setArticleTypes] = useState([]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch decisioned manuscripts
  useEffect(() => {
    const fetchManuscripts = async () => {
      setLoading(true);
      try {
        const response = await axios.post('/editors/allAcceptedSubmissions', {
          search: debouncedSearchTerm,
          decision: filterDecision,
          type: filterType
        }, {
          params: {
            page: currentPage,
            limit: itemsPerPage
          }
        });

        if (response.data.success === 'Admin Account') {
          // The backend now sends properly formatted submissions
          setManuscripts(response.data.submissions);
          setTotalItems(response.data.total);
          setTotalPages(response.data.totalPages);
          if (response.data.decisionStats) {
            setDecisionStats(response.data.decisionStats);
          }
          if (Array.isArray(response.data.articleTypes)) {
            setArticleTypes(response.data.articleTypes);
          }
        } else {
          showToast(response.data.message || 'Failed to load manuscripts', 'error');
        }
      } catch (error) {
        console.error('Error fetching manuscripts:', error);
        showToast(error.response?.data?.message || 'Failed to load manuscripts. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchManuscripts();
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterDecision, filterType, user]);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('decisioned_view', viewType);
  }, [viewType]);

  // Stats
  const stats = {
    total: totalItems,
    accepted: decisionStats.accepted,
    rejected: decisionStats.rejected,
    returnedForCorrection: decisionStats.returned_for_correction,
    returnedForRevision: decisionStats.returned_for_revision
  };

  const getDecisionColor = (decision) => {
    const colors = {
      accepted: 'bg-green-100 text-green-800 border-green-200',
      returned_for_correction:'bg-yellow-100 text-yellow-800 border-yellow-200',
      returned_for_revision:'bg-blue-100 text-blue-800 border-blue-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[decision] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getDecisionIcon = (decision) => {
    switch(decision) {
      case 'accepted':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'rejected':
        return <XCircle className="w-3 h-3 mr-1" />;
      case 'returned_for_correction':
        return <Clock className="w-3 h-3 mr-1" />;
      case 'returned_for_revision':
        return <RefreshCw className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  // Manuscripts are already filtered and paginated server-side
  const displayManuscripts = manuscripts;
  const displayTotalPages = totalPages;

  const handleViewManuscript = (manuscript) => {
    setSelectedManuscript(manuscript);
    setShowViewModal(true);
    setShowActionsDropdown(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= displayTotalPages) {
      setCurrentPage(page);
    }
  };

  const handleDownloadDecision = (manuscriptId, decision) => {
    // In real app, would call an API endpoint to download the decision letter
    showToast(`Downloading ${decision} letter for manuscript ${manuscriptId}`, 'success');
    
    // Example API call:
    // window.open(`/api/editors/download-decision/${manuscriptId}`, '_blank');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterDecision('all');
    setFilterType('all');
    setCurrentPage(1);
  };

  const handleArchiveClick = (revisionId) => {
    setArchiveSubmissionId(revisionId);
    setShowArchiveModal(true);
    setShowActionsDropdown(null);
  };

  const handleArchiveConfirm = async () => {
    setArchiveLoading(true);

    try {
      // Make API call to archive submission
      await axios.post('/editors/archiveSubmission', {
        submissionId: archiveSubmissionId
      });

      // Refresh decisioned submissions list
      const response = await axios.post('/editors/allAcceptedSubmissions', {
        search: debouncedSearchTerm,
        decision: filterDecision,
        type: filterType
      }, {
        params: {
          page: currentPage,
          limit: itemsPerPage
        }
      });

      if (response.data.success === 'Admin Account') {
        setManuscripts(response.data.submissions);
        setTotalItems(response.data.total);
        setTotalPages(response.data.totalPages);
        if (response.data.decisionStats) {
          setDecisionStats(response.data.decisionStats);
        }
        if (Array.isArray(response.data.articleTypes)) {
          setArticleTypes(response.data.articleTypes);
        }
      }

      showToast('Submission archived successfully', 'success');
    } catch (error) {
      console.error('Error archiving submission:', error);
      showToast(error.response?.data?.error || 'Failed to archive submission', 'error');
    } finally {
      setArchiveLoading(false);
      setShowArchiveModal(false);
      setArchiveSubmissionId(null);
    }
  };

  const handleArchiveCancel = () => {
    setShowArchiveModal(false);
    setArchiveSubmissionId(null);
  };

  const handleViewReviews = async (manuscript) => {
    setReviewsManuscript(manuscript);
    setShowReviewsModal(true);
    setShowActionsDropdown(null);
    setReviewsLoading(true);
    setReviewsData([]);
    setSelectedReviewIds([]);
    try {
      const response = await axios.post('/editors/reviews-by-article', {
        articleId: manuscript.revision_id
      });
      if (response.data.success) {
        setReviewsData(response.data.reviews || []);
      } else {
        showToast(response.data.message || 'Failed to load reviews', 'error');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast(error.response?.data?.message || 'Failed to load reviews. Please try again.', 'error');
    } finally {
      setReviewsLoading(false);
    }
  };

  const toggleSelectReview = (reviewId) => {
    setSelectedReviewIds((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const toggleSelectAllReviews = () => {
    const all = reviewsData.map((r) => r.review_id);
    const allSelected = all.length > 0 && all.every((id) => selectedReviewIds.includes(id));
    setSelectedReviewIds((prev) =>
      allSelected
        ? prev.filter((id) => !all.includes(id))
        : [...new Set([...prev, ...all])]
    );
  };

  const handleExportSelectedReviews = async (format) => {
    if (selectedReviewIds.length === 0) return;
    const articleId = reviewsManuscript?.revision_id;
    if (!articleId) return;
    setExportingReviews(format);
    try {
      const response = await axios.post(
        '/editors/export-all-reviews',
        { articleIds: [articleId], reviewIds: selectedReviewIds, format },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `selected_reviews.${format === 'docx' ? 'docx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast(`Selected reviewer reports exported as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Error exporting selected reviews:', error);
      showToast('Failed to export selected reviews. Please try again.', 'error');
    } finally {
      setExportingReviews(null);
    }
  };

  const handleViewFullReport = (review) => {
    const articleId = reviewsManuscript?.revision_id;
    setShowReviewsModal(false);
    setReviewsManuscript(null);
    setReviewsData([]);
    setSelectedReviewIds([]);
    navigate(`/editors/review/content/${review.review_id}/${articleId}`);
  };

  const formatReviewDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading && manuscripts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading decisioned manuscripts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <FileText className="w-6 h-6 mr-3" />
          Decisioned Manuscripts
        </h1>
        <p className="text-gray-600 mt-1">View all manuscripts that have received a decision</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Decisioned</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-[--asfi-purple] bg-opacity-10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <ThumbsUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <ThumbsDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Returned for Correction</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.returnedForCorrection}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Returned for Revision</p>
              <p className="text-2xl font-bold text-blue-600">{stats.returnedForRevision}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by title, ID, or author..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <select
              value={filterDecision}
              onChange={(e) => {
                setFilterDecision(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            >
              <option value="all">All Decisions</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="returned_for_correction">Returned for Correction</option>
              <option value="returned_for_revision">Returned for Revision</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            >
              <option value="all">All Types</option>
              {articleTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'table' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Table View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'grid' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={clearFilters}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Reset Filters"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing <span className="font-medium">{totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
            <span className="font-medium">{totalItems}</span> manuscripts
          </p>
          {(searchTerm || filterDecision !== 'all' || filterType !== 'all') && (
            <button
              onClick={clearFilters}
              className="text-[--asfi-purple] hover:text-[--asfi-blue] text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Manuscripts Display */}
      {displayManuscripts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No manuscripts found</h3>
          <p className="text-gray-500">
            {searchTerm || filterDecision !== 'all' || filterType !== 'all'
              ? 'Try adjusting your search or filters' 
              : 'No decisioned manuscripts available.'}
          </p>
        </div>
      ) : viewType === 'table' ? (
        /* Table View */
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[--asfi-purple]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Decision
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Manuscript
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Authors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Decision Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Decision Maker
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayManuscripts.map((manuscript) => (
                  <tr key={manuscript.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getDecisionColor(manuscript.decision)}`}>
                        {getDecisionIcon(manuscript.decision)}
                        {manuscript.decision.charAt(0).toUpperCase() + manuscript.decision.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{manuscript.title}</p>
                        <p className="text-xs text-[--asfi-purple] mt-1">{manuscript.id}</p>
                        {manuscript.isWomenInScience && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-pink-100 text-pink-800">
                            <Award className="w-3 h-3 mr-1" />
                            Women in Science
                          </span>
                        )}
                          {manuscript.isBelispointAcademic && (
                            <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              <Award className="w-3 h-3 mr-1" />
                              Belispoint Academic
                            </span>
                          )}
                          {manuscript.isKidnappingForRansom && (
                            <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-red-100 text-red-800">
                              <Award className="w-3 h-3 mr-1" />
                              Kidnapping For Ransom
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {manuscript.type}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 line-clamp-2 max-w-xs">
                        {manuscript.authors?.join(', ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {manuscript.decisionDate}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{manuscript.decisionMaker}</div>
                      <div className="text-xs text-gray-500">{manuscript.decisionMakerEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewManuscript(manuscript)}
                          className="p-2 text-gray-600 hover:text-white hover:bg-[--asfi-purple] hover:bg-opacity-10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewReviews(manuscript)}
                          className="p-2 text-gray-600 hover:text-[--asfi-purple] hover:bg-purple-50 rounded-lg transition-colors"
                          title="View Submitted Reviews"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadDecision(manuscript.id, manuscript.decision)}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Download Decision Letter"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setShowActionsDropdown(showActionsDropdown === manuscript.id ? null : manuscript.id)}
                            className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="More Actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {showActionsDropdown === manuscript.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-30"
                                onClick={() => setShowActionsDropdown(null)}
                              />
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-40 border border-gray-200">
                                <button
                                  onClick={() => {
                                    handleViewManuscript(manuscript);
                                    setShowActionsDropdown(null);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  View Details
                                </button>
                                <button
                                  onClick={() => {
                                    handleViewReviews(manuscript);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  View Submitted Reviews
                                </button>
                                <button
                                  onClick={() => {
                                    handleDownloadDecision(manuscript.id, manuscript.decision);
                                    setShowActionsDropdown(null);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Download Decision Letter
                                </button>
                                <button
                                  onClick={() => {
                                    handleArchiveClick(manuscript.revision_id);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Archive
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayManuscripts.map((manuscript) => (
            <div
              key={manuscript.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getDecisionColor(manuscript.decision)}`}>
                    {getDecisionIcon(manuscript.decision)}
                    {manuscript.decision.charAt(0).toUpperCase() + manuscript.decision.slice(1)}
                  </span>
                  <span className="text-xs text-gray-500">{manuscript.decisionDate}</span>
                </div>

                {/* Title and ID */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {manuscript.title}
                </h3>
                <p className="text-xs text-[--asfi-purple] font-medium mb-3">
                  {manuscript.id}
                </p>

                {/* Type */}
                <div className="flex items-center mb-3">
                  <Tag className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{manuscript.type}</span>
                </div>

                {/* Authors */}
                <div className="flex items-start mb-3">
                  <Users className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {manuscript.authors?.join(', ')}
                    </p>
                  </div>
                </div>

                {/* Decision Maker */}
                <div className="flex items-start mb-3">
                  <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{manuscript.decisionMaker}</p>
                    <p className="text-xs text-gray-500">{manuscript.decisionMakerEmail}</p>
                  </div>
                </div>

                {/* Women in Science Badge */}
                {manuscript.isWomenInScience && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-pink-100 text-pink-800 rounded">
                      <Award className="w-3 h-3 mr-1" />
                      Women in Science
                    </span>
                  </div>
                )}
                {manuscript.isBelispointAcademic && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      <Award className="w-3 h-3 mr-1" />
                      Belispoint Academic
                    </span>
                  </div>
                )}
                {manuscript.isKidnappingForRansom && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                      <Award className="w-3 h-3 mr-1" />
                      Kidnapping For Ransom
                    </span>
                  </div>
                )}


                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleViewManuscript(manuscript)}
                    className="flex-1 px-3 py-2 bg-[--asfi-purple] text-white text-sm font-medium rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <button
                    onClick={() => handleViewReviews(manuscript)}
                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Reviews
                  </button>
                  <button
                    onClick={() => handleDownloadDecision(manuscript.id, manuscript.decision)}
                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Letter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {displayTotalPages > 1 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {displayTotalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, displayTotalPages) }, (_, i) => {
                let pageNum;
                if (displayTotalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= displayTotalPages - 2) {
                  pageNum = displayTotalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-[--asfi-purple] text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === displayTotalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* View Manuscript Modal */}
      {showViewModal && selectedManuscript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Decision Details</h3>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedManuscript(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Decision Badge */}
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getDecisionColor(selectedManuscript.decision)}`}>
                    {getDecisionIcon(selectedManuscript.decision)}
                    {selectedManuscript.decision.charAt(0).toUpperCase() + selectedManuscript.decision.slice(1)}
                  </span>
                  <span className="text-sm text-gray-500">on {selectedManuscript.decisionDate}</span>
                  {selectedManuscript.isWomenInScience && (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-pink-100 text-pink-800 rounded-full border border-pink-200">
                      <Award className="w-4 h-4 mr-1" />
                      Women in Science
                    </span>
                  )}
                  {selectedManuscript.isBelispointAcademic && (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full border border-blue-200">
                      <Award className="w-4 h-4 mr-1" />
                      Belispoint Academic
                    </span>
                  )}
                  {selectedManuscript.isKidnappingForRansom && (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full border border-red-200">
                      <Award className="w-4 h-4 mr-1" />
                      Kidnapping For Ransom
                    </span>
                  )}
                </div>

                {/* Title and ID */}
                <div>
                  <p className="text-sm font-medium text-[--asfi-purple] mb-1">
                    {selectedManuscript.id}
                  </p>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedManuscript.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{selectedManuscript.type}</p>
                </div>

                {/* Decision Maker */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Decision Made By</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{selectedManuscript.decisionMaker}</p>
                      <p className="text-sm text-gray-600 flex items-center mt-1">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedManuscript.decisionMakerEmail}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Decision Notes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Decision Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    {selectedManuscript.decisionNotes}
                  </p>
                </div>

                {/* Authors */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Authors</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedManuscript.authors?.map((author, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {author}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Corresponding Author */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Corresponding Author</h4>
                  <p className="text-sm text-gray-900">{selectedManuscript.correspondingAuthor}</p>
                  <p className="text-sm text-gray-600 flex items-center mt-1">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    {selectedManuscript.correspondingEmail}
                  </p>
                </div>

                {/* Abstract with Quill parsing */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Abstract</h4>
               
                  <RichTextViewer content={selectedManuscript.abstract} />
                </div>

                {/* Keywords */}
                {selectedManuscript.keywords && selectedManuscript.keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Tag className="w-4 h-4 mr-2" />
                      Keywords
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedManuscript.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-50 text-[--asfi-purple] text-sm rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Submitted Date</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      {selectedManuscript.submittedDate}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Decision Date</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      {selectedManuscript.decisionDate}
                    </p>
                  </div>
                </div>

                {/* Files */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Paperclip className="w-4 h-4 mr-2" />
                    Files
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(selectedManuscript.files || {}).filter(([_, value]) => value).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-700 capitalize">
                            {key.replace(/_/g, ' ')}:
                          </span>
                          <span className="ml-2 text-sm text-gray-600">{value}</span>
                        </div>
                        <button className="p-1 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded transition-colors" onClick={() => {handleDownloadFile(value, key)}}>
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDownloadDecision(selectedManuscript.id, selectedManuscript.decision);
                    setShowViewModal(false);
                  }}
                  className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Decision Letter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Archive Submission</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to archive this submission?
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Archived submissions can be restored later from the Archived Papers section. Authors will be notified about this action.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleArchiveCancel}
                  disabled={archiveLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveConfirm}
                  disabled={archiveLoading}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {archiveLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Confirm Archive
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Modal */}
      {showReviewsModal && reviewsManuscript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                    Submitted Reviews
                  </h3>
                  <p className="text-xs text-[--asfi-purple] mt-1">{reviewsManuscript.revision_id}</p>
                </div>
                <button
                  onClick={() => {
                    setShowReviewsModal(false);
                    setReviewsManuscript(null);
                    setReviewsData([]);
                    setSelectedReviewIds([]);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {reviewsManuscript.title}
              </h2>

              {reviewsLoading ? (
                <div className="py-16 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
                  <p className="mt-4 text-gray-600">Loading reviews...</p>
                </div>
              ) : reviewsData.length === 0 ? (
                <div className="py-16 text-center">
                  <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No submitted reviews found</h3>
                  <p className="text-gray-500">
                    This manuscript does not have any submitted reviewer reports yet.
                  </p>
                </div>
              ) : (
                <>
                  {/* Select All + Export Toolbar */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reviewsData.length > 0 && reviewsData.every((r) => selectedReviewIds.includes(r.review_id))}
                        onChange={toggleSelectAllReviews}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-purple-800">
                        {selectedReviewIds.length > 0
                          ? `${selectedReviewIds.length} report${selectedReviewIds.length > 1 ? 's' : ''} selected`
                          : 'Select all reports'}
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleExportSelectedReviews('pdf')}
                        disabled={selectedReviewIds.length === 0 || !!exportingReviews}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {exportingReviews === 'pdf' ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <FileText className="w-3.5 h-3.5" />
                        )}
                        Export PDF
                      </button>
                      <button
                        onClick={() => handleExportSelectedReviews('docx')}
                        disabled={selectedReviewIds.length === 0 || !!exportingReviews}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {exportingReviews === 'docx' ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        Export Word
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {reviewsData.map((review, index) => (
                      <div key={review.review_id} className={`border rounded-lg p-4 ${selectedReviewIds.includes(review.review_id) ? 'border-purple-400 bg-purple-50/50' : 'border-gray-200'}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedReviewIds.includes(review.review_id)}
                              onChange={() => toggleSelectReview(review.review_id)}
                              className="w-4 h-4 mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              title="Select report"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Reviewer Report {index + 1}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {review.reviewer_name} ({review.reviewer_email})
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Submitted: {formatReviewDate(review.submittedDate)}
                              </p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                            (review.recommendation || '').toLowerCase().includes('accept')
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : (review.recommendation || '').toLowerCase().includes('reject')
                                ? 'bg-red-100 text-red-800 border-red-200'
                                : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}>
                            {review.recommendation}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-lg font-bold text-gray-900">{review.section1Total}/115</p>
                            <p className="text-xs text-gray-500">Detailed Scoring</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-lg font-bold text-gray-900">{review.section2Total}/25</p>
                            <p className="text-xs text-gray-500">Overall Rating</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-lg font-bold text-[--asfi-purple]">{review.overallTotal}/140</p>
                            <p className="text-xs text-gray-500">Overall Total</p>
                          </div>
                        </div>

                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => handleViewFullReport(review)}
                            className="inline-flex items-center px-4 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View Full Report
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => {
                    setShowReviewsModal(false);
                    setReviewsManuscript(null);
                    setReviewsData([]);
                    setSelectedReviewIds([]);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default Decisioned;